import { Tabs as MuiTabs, TabsOwnProps, Theme } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";

export interface TabsProps extends TabsOwnProps {
  underline?: boolean;
  size?: "small" | "medium" | "large";
  color?: "primary" | "secondary";
}

const getCommonStyles = (
  theme: Theme,
  size: NonNullable<TabsProps["size"]>,
) => {
  const { typography, spacing } = theme;

  const heightMap = {
    large: 9,
    medium: 7,
    small: 6,
  };

  return {
    width: "fit-content",

    "& .MuiTabs-flexContainer .MuiTab-root": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      height: spacing(heightMap[size]),
      padding: size === "large" ? spacing(3) : spacing(1, 2),
      columnGap: "5px",
      fontSize:
        size === "small" ? typography.mini.fontSize : typography.small.fontSize,
      textTransform: "none",
      borderWidth: "0px",
      borderRadius: spacing(1),

      "& .MuiTab-iconWrapper": {
        marginBottom: 0,
      },
    },
  };
};

const getUnderlineStyles = (
  theme: Theme,
  size: NonNullable<TabsProps["size"]>,
) => {
  const { palette, spacing } = theme;

  const marginMap = {
    large: 4,
    medium: 3,
    small: 2,
  };

  return {
    backgroundColor: "transparent",

    "& .MuiTabs-scroller .MuiTabs-flexContainer .MuiTab-root": {
      marginBottom: spacing(marginMap[size]),
      fontWeight: 500,
      color: palette.secondary[3],
      marginRight: spacing(2),

      "&:last-child": {
        marginRight: 0,
      },

      "&:hover": {
        backgroundColor: palette.secondary[8],
        boxShadow: `inset 0 0 0 0.5px ${theme.palette.secondary[7]}`,
      },

      "&.Mui-selected": {
        backgroundColor: palette.secondary[7],
        boxShadow: `inset 0 0 0 0.5px ${theme.palette.secondary[6]}`,
        color: palette.secondary[1],
      },
    },

    "& .MuiTabs-indicator": {
      height: "1px",
      backgroundColor: palette.secondary[5],
    },
  };
};

const getDefaultTabStyles = (theme: Theme, color: TabsProps["color"]) => {
  const { palette, spacing } = theme;

  return {
    borderRadius: spacing(1),
    "& .MuiTabs-scroller .MuiTabs-flexContainer .MuiTab-root": {
      fontWeight: 400,
      color: palette.secondary[3],

      "&:hover": {
        backgroundColor:
          color === "primary"
            ? palette.secondary["5/50"]
            : palette.secondary[5],
      },

      "&:last-child": {
        marginRight: 0,
      },

      "&.Mui-selected": {
        backgroundColor:
          color === "primary" ? palette.secondary[5] : palette.secondary[6],
        boxShadow: `inset 0 0 0 0.5px ${theme.palette.secondary["3/25"]}`,
        color: palette.secondary[1],
      },
    },

    "& .MuiTabs-indicator": {
      display: "none",
    },
  };
};

const StyledTabs = styled(MuiTabs, {
  shouldForwardProp: (prop) =>
    prop !== "underline" && prop !== "color" && prop !== "size",
})<TabsProps>(({
  theme,
  underline = false,
  color = "secondary",
  size = "medium",
}) => {
  const commonStyles = getCommonStyles(theme, size);
  const tabStyles = underline
    ? getUnderlineStyles(theme, size)
    : getDefaultTabStyles(theme, color);
  return { ...commonStyles, ...tabStyles };
});

export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  ({ underline = false, size = "medium", ...rest }: TabsProps, ref) => {
    return <StyledTabs {...rest} ref={ref} underline={underline} size={size} />;
  },
);
